import { gql } from '@apollo/client'
import { BrandJusIa, Card, Chip, Heading } from '@farol-ds/react'
import { Price } from '@jusbrasil-web/shared-price'
import classNames from 'classnames'

import { PlanFrequency } from '../../../constants'
import { BenefitsList } from '../../benefits-list'
import { CheckoutButton } from '../../checkout-button'
import { CheckoutButton_OfferLockCardFragment } from '../../checkout-button/__generated__/checkout-button.graphql'
import { OneClickButton } from '../../one-click-button'
import {
  JurisThreeOffersPlanCardPaywall_OfferLockCardFragment,
  JurisThreeOffersPlanCardPaywall_ViewerFragment,
} from './__generated__/juris-three-offers-switch-frequency-plan-card-paywall.graphql'
import styles from './juris-three-offers-switch-frequency-plan-card-paywall.module.scss'

export interface JurisThreeOffersSwitchFrequencyLockPlanCardPaywallProps {
  offerLockCard: JurisThreeOffersPlanCardPaywall_OfferLockCardFragment
  viewer: JurisThreeOffersPlanCardPaywall_ViewerFragment
  showCreditCardAndRemoveOneClick?: boolean
}

export function JurisThreeOffersSwitchFrequencyLockPlanCardPaywall(
  props: JurisThreeOffersSwitchFrequencyLockPlanCardPaywallProps
) {
  const { offerLockCard, showCreditCardAndRemoveOneClick, viewer } = props

  const { title, offer } = offerLockCard
  const {
    priceText,
    priceTitle,
    frequency,
    isJusAIOffer,
    priceAfterPromotionalPeriodText,
    frequencyPeriodText,
  } = offer

  const isYearlyPlan = frequency === PlanFrequency.YEARLY
  const isPromotional = !!priceAfterPromotionalPeriodText

  const getCustomTitle = (title: string): string => {
    if (title.includes('Avançado + Jus IA')) return 'Avançado +'
    return title
  }

  const kindSubmitButton = isJusAIOffer ? 'primary' : 'secondary'

  const offerLockCardWithoutCCToken = {
    ...offerLockCard,
    creditCard: {
      ...offerLockCard.creditCard,
      ccToken: null,
    } as CheckoutButton_OfferLockCardFragment['creditCard'],
  }

  return (
    <div className={styles.jurisThreeOffersPlanCard}>
      {isJusAIOffer && (
        <Chip size="md" kind="secondary" className={styles.jurisThreeOffersPlanCardChipRecommended}>
          Recomendado
        </Chip>
      )}
      <Card
        className={classNames(
          styles.jurisThreeOffersPlanCardCard,
          isJusAIOffer && styles.jurisThreeOffersPlanCardCardJusAI
        )}
        disablePadding
      >
        <div className={styles.jurisThreeOffersPlanCardCardBody}>
          <div className={styles.jurisThreeOffersPlanCardBody}>
            <div className={styles.jurisThreeOffersPlanCardMainSection}>
              <div className={styles.jurisThreeOffersPlanCardTitle}>
                <Heading size="md" asChild>
                  <h4>{getCustomTitle(title)}</h4>
                </Heading>

                {isJusAIOffer && (
                  <BrandJusIa width={95} className={styles.jurisThreeOffersPlanCardJusAILogo} />
                )}
              </div>

              <Price className={styles.jurisThreeOffersPlanCardPrice}>
                {priceTitle && (
                  <Price.Title>
                    <span dangerouslySetInnerHTML={{ __html: priceTitle }} />
                  </Price.Title>
                )}

                {isPromotional ? (
                  <>
                    <Price.Value>{priceText}</Price.Value>
                    <Price.Helptext>No {frequencyPeriodText}</Price.Helptext>
                  </>
                ) : (
                  <span>
                    <Price.Value>{priceText}</Price.Value>
                    {!isYearlyPlan && frequency && (
                      <Price.ValueFrequency>{frequency}</Price.ValueFrequency>
                    )}
                  </span>
                )}
              </Price>

              <div className={styles.jurisThreeOffersPlanCardCheckoutButtonSection}>
                {showCreditCardAndRemoveOneClick ? (
                  <>
                    <CheckoutButton kind={kindSubmitButton} offerLockCard={offerLockCard} />
                    <CheckoutButton
                      offerLockCard={offerLockCardWithoutCCToken}
                      kind={kindSubmitButton}
                    />
                  </>
                ) : (
                  <>
                    <CheckoutButton kind={kindSubmitButton} offerLockCard={offerLockCard} />
                    <OneClickButton
                      offerLockCard={offerLockCard}
                      viewer={viewer}
                      kind={kindSubmitButton}
                    />
                  </>
                )}
              </div>

              <div className={styles.jurisThreeOffersPlanCardBenefitsSection}>
                <BenefitsList offerLockCard={offerLockCard} />
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}

JurisThreeOffersSwitchFrequencyLockPlanCardPaywall.fragments = {
  offerLockCard: gql`
    fragment JurisThreeOffersPlanCardPaywall_offerLockCard on OfferLockCard {
      title
      subtitle
      offer {
        priceText
        priceTitle
        frequency
        isJusAIOffer
        frequencyPeriodText
        priceAfterPromotionalPeriodText
      }
      submitData {
        submitUrl
        jwt
      }
      ...BenefitsList_offerLockCard
      ...CheckoutButton_offerLockCard
      ...OneClickButton_offerLockCard
    }
    ${BenefitsList.fragments.offerLockCard}
    ${CheckoutButton.fragments.offerLockCard}
    ${OneClickButton.fragments.offerLockCard}
  `,
  viewer: gql`
    fragment JurisThreeOffersPlanCardPaywall_viewer on Profile {
      pid
      ...OneClickButton_viewer
    }
    ${OneClickButton.fragments.viewer}
  `,
}
